<template>
  <section class="card">
    <header class="card-hdr">
      <h3>Current Location</h3>
      <div class="card-tools">
        <button class="ico-btn-card" title="Refresh" @click="$emit('refresh')">
          <svg width="14" height="14" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M17.65 6.35A7.95 7.95 0 0 0 12 4V1L7 6l5 5V7c2.76 0 5 2.24 5 5a5 5 0 0 1-8.66 3.54l-1.42 1.42A7 7 0 1 0 19 12c0-1.93-.78-3.68-2.05-4.95z"
            />
          </svg>
        </button>
        <button
          class="ico-btn-card"
          :disabled="!isEnabled || !hasLocation"
          title="Centra"
          @click="handleCenterClick"
        >
          <svg width="14" height="14" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M12 8a4 4 0 1 1 0 8a4 4 0 0 1 0-8m9 3h-2.05a7 7 0 0 0-5.95-5.95V3h-2v2.05A7 7 0 0 0 5.05 11H3v2h2.05A7 7 0 0 0 11 18.95V21h2v-2.05A7 7 0 0 0 18.95 13H21z"
            />
          </svg>
        </button>
        <button
          class="ico-btn-card"
          :disabled="!isEnabled"
          title="Dettagli"
          @click="$emit('open-modal')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24">
            <path fill="currentColor" d="M11 7h2v2h-2zm0 4h2v6h-2z" />
            <path
              fill="currentColor"
              d="M12 2a10 10 0 1 0 10 10A10.01 10.01 0 0 0 12 2m0 18a8 8 0 1 1 8-8a8.01 8.01 0 0 1-8 8"
            />
          </svg>
        </button>
      </div>
    </header>

    <div v-if="isEnabled" class="card-body mini-map">
      <l-map
        ref="mapRef"
        :zoom="initialZoom"
        :center="hasLocation ? (location as any) : defaultCenter"
        :options="{
          zoomControl: false,
          attributionControl: false,
          scrollWheelZoom: false,
          dragging: true,
        }"
        style="height: 220px; width: 100%; border-radius: 8px; overflow: hidden"
      >
        <l-tile-layer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <l-marker v-if="hasLocation" :lat-lng="location!" />
      </l-map>

      <div class="address-box" v-if="address">
        <span class="addr-icon">📍</span>
        <span class="addr-text">{{ address }}</span>
      </div>

      <div v-if="!hasLocation" class="no-loc">
        <Placeholder label="Posizione non disponibile per questo device" />
      </div>
    </div>

    <div v-else class="card-body">
      <Placeholder :label="`Nessun dato in ${'modulo'}`" />
    </div>
  </section>
</template>

<script setup lang="ts">
import Placeholder from "@/components/ui/Placeholder.vue";
import { LMap, LMarker, LTileLayer } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import { computed, nextTick, ref, watch } from "vue";
import { useToast } from "vue-toast-notification";

defineOptions({ name: "LocationCard" });

const props = defineProps<{
  isEnabled: boolean;
  deviceName: string;

  location?: [number, number] | undefined;
  address?: string;
}>();

const emit = defineEmits<{
  (e: "refresh"): void;
  (e: "center"): void;
  (e: "open-modal"): void;
}>();

const toast = useToast();
const defaultCenter: [number, number] = [45.41, 11.88];
const initialZoom = 12;
const hasLocation = computed(
  () => Array.isArray(props.location) && props.location.length === 2
);

// Riferimento alla mappa
const mapRef = ref<InstanceType<typeof LMap>>();

// Funzione per centrare immediatamente sulla posizione del device
const handleCenterClick = () => {
  if (!hasLocation.value || !mapRef.value?.leafletObject)
    return toast.success(
      `Impossibile centrare la mappa sulla posizione del dispositivo per mancanza di coordinate, imposta prima la modalità a 'Dinamico'`,
      { duration: 2500 }
    );

  const map = mapRef.value.leafletObject;

  // Centra immediatamente sul marker con zoom ottimale
  map.setView(props.location!, 14, {
    animate: true,
    duration: 0.5, // Durata più breve per un effetto più immediato
    easeLinearity: 0.25,
  });

  // Emetti l'evento al componente padre
  emit("center");
};

// Quando la posizione cambia, aggiorna automaticamente il centro della mappa
watch(
  () => props.location,
  (newLocation) => {
    if (newLocation && hasLocation.value && mapRef.value?.leafletObject) {
      const map = mapRef.value.leafletObject;
      // Aggiorna il centro ma mantieni lo zoom corrente
      map.setView(newLocation, map.getZoom(), {
        animate: false, // Senza animazione per aggiornamenti automatici
      });
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.address-box {
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.9rem;
  color: #c4c7c5;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 12px;
  border-radius: 6px;
  line-height: 1.4;
}

.addr-icon {
  font-size: 1rem;
  opacity: 0.8;
  margin-top: 2px;
}

.addr-text {
  flex: 1;
}
</style>
